import { cn } from '../../lib/helpers.ts';
import {
    Dimension,
    DIMENSION_LABELS,
    DIMENSION_STYLES,
} from '../../lib/plan/types';

interface DimensionFeedbackProps {
    dimension: Dimension;
    score: number;
    isPrimary?: boolean;
    previousScore?: number;
}

// Finální znění od psycholožky (2026-08-16).
// Tón: měkký, nehodnotící — rámováno jako prostor pro růst, ne jako nedostatek.
const FEEDBACK_TEXTS: Record<Dimension, { low: string; mid: string; high: string }> = {
    koncentrace: {
        low: 'Soustředění je teď oblast, kde má vaše třída největší prostor k růstu. Krátká zklidňující a pohybová cvičení před prací pomohou žákům lépe se naladit na výuku.',
        mid: 'Třída se dokáže soustředit, i když to občas vyžaduje vaši podporu. Pravidelné krátké rituály na začátku hodin tuto dovednost dál posílí.',
        high: 'Soustředění patří mezi silné stránky vaší třídy. Žáci se snadno naladí na výuku bez častého vyrušování a neklidu. Soustředěnost stačí udržovat drobnými rituály, které už žáci znají.',
    },
    atmosfera: {
        low: 'Atmosféra ve třídě má teď prostor pro zklidnění a uvolnění — žáci mohou být vnímavější k napětí, obavám a hodnocení. Cvičení zaměřená na vytváření pocitu bezpečí a klidu jim pomohou se uvolnit.',
        mid: 'Atmosféra ve třídě bývá většinou přívětivá, i když v náročnějších chvílích občas zakolísá. Pravidelná relaxační cvičení pomohou udržet pohodu a stabilitu i během náročnějších dnů.',
        high: 'Bezpečná a přijímající atmosféra patří k nejsilnějším stránkám vaší třídy. Žáci se ve třídě cítí dobře a jistě i v náročnějších chvílích. Pokračujte v zavedené rutině, která u žáků upevňuje vzájemnou důvěru.',
    },
    vztahy: {
        low: 'Vztahy mezi žáky jsou oblastí, kde má vaše třída největší prostor k růstu. Cvičení na spolupráci, rozvoj empatie a vzájemná ocenění pomohou kolektivu držet pohromadě.',
        mid: 'Vztahy ve třídě fungují dobře, i když v zátěžových situacích občas vzniká drobné pnutí nebo neshody. Pravidelná cvičení zaměřená na spolupráci a rozvoj empatie tuto dovednost posílí a upevní vzájemné vazby.',
        high: 'Vztahy v kolektivu patří mezi výrazně silné stránky vaší třídy. Žáci si dokážou přirozeně pomáhat, respektují se a drží při sobě. Nadále podporujte tuto soudržnost prostorem pro společnou reflexi a oceňování vzájemné pomoci.',
    },
    emoce: {
        low: 'Práce s emocemi je oblast, kde má vaše třída největší prostor k růstu. Silné emoce mohou ovlivňovat soustředění i spolupráci mezi žáky. Cvičení na uvědomění a zvládání emocí žákům pomohou lépe porozumět tomu, co prožívají, a naučí je, jak s pocity bezpečně zacházet.',
        mid: 'Žáci si své emoce uvědomují a většinou se s nimi vypořádají dobře, i když jim občas chybí slova nebo prostor k zastavení. Pravidelná cvičení pro podporu seberegulace a sdílení pocitů tyto dovednosti dál podpoří.',
        high: 'Práce s emocemi je silnou stránkou vaší třídy. Žáci dokážou své pocity pojmenovat, přijmout je a dále s nimi pracovat. Rozvíjejte tuto dovednost i nadále nabídkou prostoru pro otevřenou reflexi a vzájemné naslouchání.',
    },
};

function getBand(score: number): 'low' | 'mid' | 'high' {
    if (score < 2) return 'low';
    if (score < 3) return 'mid';
    return 'high';
}

export function DimensionFeedback({
    dimension,
    score,
    isPrimary = false,
    previousScore,
}: DimensionFeedbackProps) {
    const percentage = Math.round((score / 4) * 100);
    const text = FEEDBACK_TEXTS[dimension][getBand(score)];

    // Škála je 0–4, rozdíl ukazujeme ve stejných procentech jako samotné skóre.
    const delta =
        previousScore === undefined
            ? null
            : Math.round(((score - previousScore) / 4) * 100);

    const style = DIMENSION_STYLES[dimension];

    return (
        // Barvu nese každá dimenze sama, primární se odlišuje prstencem —
        // kdyby ji nesla taky barva, splynulo by „primární" s „emoce".
        <div
            className={cn(
                'rounded-xl px-4 py-3',
                style.card,
                isPrimary && 'ring-2 ring-inset ring-ink-900/15'
            )}
        >
            <p className="text-sm font-semibold text-ink-900">
                {DIMENSION_LABELS[dimension]} · {percentage} %
                {isPrimary && (
                    <span className="ml-2 rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-ink-800">
                        váš hlavní směr
                    </span>
                )}
                {delta !== null && delta !== 0 && (
                    <span
                        className={cn(
                            'ml-2 text-xs font-medium',
                            // Zhoršení se nebarví červeně — program je prevence,
                            // ne hodnocení učitele.
                            delta > 0 ? 'text-product-600' : 'text-ink-700'
                        )}
                    >
                        {delta > 0 ? '↑' : '↓'} {Math.abs(delta)} b. od minule
                    </span>
                )}
            </p>
            <p className="text-sm text-ink-800">{text}</p>
        </div>
    );
}
